<script lang="ts">
  import { Popper } from "#lib/components/ui/popper/index"
  import { getUiclContext } from "#lib/internal/helpers/uiclContext"
  import type { BaseProps } from "#lib/internal/types"
  import { cn } from "#lib/utils"
  import { onMount, type Snippet } from "svelte"
  import SelectChip from "./SelectChip.svelte"
  import SelectChips from "./SelectChips.svelte"
  import Content from "./SelectContent.svelte"
  import Indicator from "./SelectIndicator.svelte"
  import { getSelectRootContext, type Option } from "./SelectRoot.svelte"
  import { selectTriggerVariants } from "./variants"

  type Props = Omit<BaseProps<"button">, "children"> & {
    behavior?: "toggle" | "open"
    ref?: HTMLElement
    children?: Snippet<[{ selected: Option | Option[] | null }]>
    triggerIndicator?: Snippet
    placeholder?: Snippet
  }

  const rootCtx = getSelectRootContext()

  let {
    ref = $bindable(),
    class: className = "",
    children,
    triggerIndicator,
    placeholder,
    ...restProps
  }: Props = $props()

  let hiddenChips = $state(0)
  let contentRef: HTMLSpanElement | undefined = $state(undefined)

  onMount(() => {
    if (ref) {
      rootCtx.triggerRef = ref
    }
  })

  function handlePointerUp(event: PointerEvent) {
    if (rootCtx.inputRef) {
      rootCtx.openSelectPanel()
      rootCtx.inputRef.focus()
      event.stopPropagation()
    } else {
      rootCtx.toggleDialog()
    }
  }

  const v2 = !!getUiclContext()?.v2
</script>

<!-- HACK: pressing space generated an onclick event, which is insane, use onmouseup -->
<Popper.Anchor
  {...restProps as any}
  onpointerup={handlePointerUp}
  as="button"
  aria-haspopup="listbox"
  aria-expanded={rootCtx.open}
  id="{rootCtx.idPrefix}-trigger"
  bind:ref
  disabled={rootCtx.disabled}
  tabindex={rootCtx.inputRef ? -1 : 0}
  class={cn(selectTriggerVariants({ size: rootCtx.size, v2 }), className)}
  onkeydown={rootCtx.handleKeyDown}
>
  {#if children}
    {@render children({ selected: rootCtx.selected })}
  {:else}
    <Content bind:content={contentRef} hiddenContent={!!hiddenChips}>
      {#if Array.isArray(rootCtx.selected) && rootCtx.selected.length > 0}
        <SelectChips bind:contentRef bind:hiddenChips />
      {:else if !Array.isArray(rootCtx.selected) && rootCtx.selected}
        {rootCtx.selected.label}
      {:else}
        <span
          class="text-general-secondary-label group-data-[disabled=true]:text-icon-general-disabled group-data-[selected=true]:text-icon-general-primary"
        >
          {#if placeholder}
            {@render placeholder()}
          {:else}
            {rootCtx.placeholder}
          {/if}
        </span>
      {/if}
    </Content>
    {#if hiddenChips}
      <SelectChip class="absolute right-7" label={`+${hiddenChips}`} />
    {/if}
  {/if}

  {#if triggerIndicator}
    {@render triggerIndicator()}
  {:else}
    <Indicator />
  {/if}
</Popper.Anchor>
